import { knowledgeExamples } from "./knowledgeExamples";

const cards = [
  {
    slug: "l4-vs-l7-load-balancing",
    title: "四层与七层负载均衡",
    category: "网络",
    level: "入门",
    summary: "TCP/UDP 监听通常工作在四层；HTTP/HTTPS 监听能够理解应用协议，通常工作在七层。",
    takeaway: "判断负载均衡层级要看监听协议和转发能力，不能只看软件名称。",
    points: ["四层依据 IP、端口和传输层连接转发", "七层可以依据域名、路径和 Header 路由", "HAProxy 既能工作在 TCP 模式，也能工作在 HTTP 模式"],
    commands: ["ss -lntp", "curl -I http://<负载均衡IP>", "sudo tcpdump -nn 'tcp port 80'"],
    pitfall: "“Nginx 一定是七层”并不严谨；stream 模块也可以代理 TCP。",
    scenario: { label: "部署 HAProxy 负载集群", template: "haproxy-web-cluster" },
  },
  {
    slug: "tcp-handshake",
    title: "TCP 三次握手",
    category: "网络",
    level: "入门",
    summary: "客户端与服务端通过 SYN、SYN-ACK、ACK 建立一个可靠的双向连接。",
    takeaway: "端口处于 LISTEN 只说明本机进程在监听，不代表客户端一定能够完成握手。",
    points: ["SYN 发起连接", "SYN-ACK 表示服务端可达且愿意建立连接", "ACK 完成连接建立"],
    commands: ["ss -antp", "nc -vz <IP> <端口>", "sudo tcpdump -nn 'tcp[tcpflags] & (tcp-syn|tcp-ack) != 0'"],
    pitfall: "安全组、主机防火墙、路由和服务绑定地址都可能让握手失败。",
  },
  {
    slug: "tcp-vs-udp",
    title: "TCP 与 UDP",
    category: "网络",
    level: "入门",
    summary: "TCP 面向连接并保证有序可靠；UDP 不建立连接，开销更小但不保证送达。",
    takeaway: "协议选择取决于业务对可靠性、延迟和重传机制的要求。",
    points: ["HTTP/1.1 和 HTTP/2 通常基于 TCP", "DNS 查询通常优先使用 UDP", "HTTP/3 基于 QUIC，而 QUIC 使用 UDP"],
    commands: ["ss -lntup", "dig azlinux.com", "curl -I https://azlinux.com"],
    pitfall: "UDP 没有连接状态，不能用 TCP 的排障思路机械套用。",
  },
  {
    slug: "dns-resolution",
    title: "DNS 解析过程",
    category: "网络",
    level: "入门",
    summary: "DNS 把域名转换为地址，解析可能经过本机缓存、递归服务器和权威服务器。",
    takeaway: "网页打不开不一定是服务故障，也可能是 DNS 解析、缓存或记录配置异常。",
    points: ["A/AAAA 记录指向地址", "CNAME 提供别名", "TTL 决定缓存可保留的时间"],
    commands: ["dig azlinux.com", "getent hosts azlinux.com", "resolvectl status"],
    pitfall: "修改 DNS 记录后，各级缓存不会同时立即失效。",
  },
  {
    slug: "http-status-codes",
    title: "HTTP 状态码怎么判断故障",
    category: "网络",
    level: "入门",
    summary: "2xx 表示成功，4xx 多与请求或权限有关，5xx 通常表示服务端处理失败。",
    takeaway: "状态码是排障入口，不是根因；还要结合响应头、日志和上游状态。",
    points: ["404 表示目标资源未找到", "502 常见于代理无法得到有效上游响应", "504 常见于代理等待上游超时"],
    commands: ["curl -sv https://azlinux.com/", "curl -I https://azlinux.com/", "tail -f /var/log/nginx/error.log"],
    pitfall: "CDN、网关、Nginx 和应用都可能生成相同的状态码。",
    scenario: { label: "部署 Nginx 环境", template: "nginx-single" },
  },
  {
    slug: "reverse-proxy",
    title: "正向代理与反向代理",
    category: "网络",
    level: "入门",
    summary: "正向代理代表客户端访问外部；反向代理代表服务端接收请求并转发给后端。",
    takeaway: "用户通常知道反向代理的地址，却不需要知道真实后端实例。",
    points: ["正向代理常用于统一出口", "反向代理常用于入口、TLS 和负载均衡", "Nginx 与 HAProxy 都可承担反向代理"],
    commands: ["curl -x http://<代理IP>:<端口> https://example.com", "nginx -T", "curl -H 'Host: example.com' http://<代理IP>"],
    pitfall: "反向代理正常监听，不代表其 upstream 后端健康。",
    scenario: { label: "部署 Nginx 环境", template: "nginx-single" },
  },
  {
    slug: "process-thread-service",
    title: "进程、线程与服务",
    category: "Linux",
    level: "入门",
    summary: "进程是运行中的程序实例，线程是进程内的执行单元，服务是受系统管理的长期进程。",
    takeaway: "排查服务故障时，应同时检查服务管理器状态、进程和监听端口。",
    points: ["PID 标识进程", "同一进程可包含多个线程", "systemd 负责启动、停止和监督服务"],
    commands: ["ps -ef", "systemctl status nginx", "pstree -p", "ss -lntp"],
    pitfall: "systemctl 显示 active 不一定代表业务接口健康。",
  },
  {
    slug: "linux-load-average",
    title: "Load Average 不等于 CPU 使用率",
    category: "Linux",
    level: "进阶",
    summary: "Linux Load 包含可运行任务和不可中断睡眠任务，磁盘 IO 卡顿也可能推高负载。",
    takeaway: "负载高时先判断任务在等待 CPU 还是等待 IO。",
    points: ["1、5、15 分钟负载反映不同时间窗口", "负载需要结合 CPU 核数理解", "D 状态任务常见于 IO 等待"],
    commands: ["uptime", "top", "vmstat 1", "ps -eo state,pid,comm,wchan:32 | awk '$1==\"D\"'"],
    pitfall: "看到 Load 高就扩容 CPU，可能完全解决不了存储问题。",
  },
  {
    slug: "memory-cache-swap",
    title: "内存、缓存与 Swap",
    category: "Linux",
    level: "进阶",
    summary: "Linux 会利用空闲内存做缓存；available 通常比 free 更能反映可用内存。",
    takeaway: "内存使用率高不等于内存不足，要结合 available、Swap 和回收行为判断。",
    points: ["page cache 可被内核回收", "Swap 频繁换入换出会影响延迟", "OOM Killer 会在严重不足时终止进程"],
    commands: ["free -h", "vmstat 1", "cat /proc/meminfo", "dmesg -T | grep -i oom"],
    pitfall: "手工清缓存通常不是解决内存问题的长期办法。",
  },
  {
    slug: "disk-space-vs-inode",
    title: "磁盘空间与 inode",
    category: "Linux",
    level: "入门",
    summary: "磁盘既可能耗尽数据空间，也可能因为大量小文件耗尽 inode。",
    takeaway: "遇到 No space left on device 时必须同时检查容量和 inode。",
    points: ["df -h 查看容量", "df -i 查看 inode", "日志、缓存和临时文件是常见增长源"],
    commands: ["df -h", "df -i", "du -xhd1 /var | sort -h", "find /var -xdev -type f | wc -l"],
    pitfall: "du 与 df 结果差异很大时，应检查已删除但仍被进程打开的文件。",
  },
  {
    slug: "deleted-file-space",
    title: "删除文件后磁盘为何没有释放",
    category: "Linux",
    level: "进阶",
    summary: "进程仍持有已删除文件的文件描述符时，目录项消失了，但数据块不会立即释放。",
    takeaway: "使用 lsof 找到持有 deleted 文件的进程，再安全重启或重新打开文件。",
    points: ["rm 删除目录项", "打开的文件描述符仍然有效", "进程关闭描述符后空间才会释放"],
    commands: ["lsof +L1", "ls -l /proc/<PID>/fd", "df -h"],
    pitfall: "不要为了释放一个日志文件直接重启整台生产服务器。",
  },
  {
    slug: "systemd-troubleshooting",
    title: "systemd 服务启动失败怎么查",
    category: "Linux",
    level: "入门",
    summary: "先查看单元状态和日志，再检查配置、权限、依赖和端口冲突。",
    takeaway: "服务失败信息通常已经在 systemctl status 或 journalctl 中留下线索。",
    points: ["ExecStart 决定启动命令", "After 不等于强依赖", "修改 unit 后需要 daemon-reload"],
    commands: ["systemctl status <服务>", "journalctl -u <服务> -n 100 --no-pager", "systemctl cat <服务>", "systemd-analyze verify <unit文件>"],
    pitfall: "反复执行 restart 会覆盖现场线索，也可能扩大故障。",
  },
  {
    slug: "time-wait",
    title: "TIME_WAIT 是什么",
    category: "Linux",
    level: "进阶",
    summary: "主动关闭 TCP 连接的一方会暂时保留连接状态，避免旧报文干扰后续连接。",
    takeaway: "TIME_WAIT 本身是正常机制，数量多时要先分析连接模型和复用策略。",
    points: ["通常出现在主动关闭方", "短连接会产生大量 TIME_WAIT", "盲目修改内核参数可能引入网络问题"],
    commands: ["ss -ant state time-wait", "ss -s", "sar -n TCP,ETCP 1"],
    pitfall: "不要看到 TIME_WAIT 多就直接认为服务器遭到攻击。",
  },
  {
    slug: "database-primary-replica",
    title: "数据库主库与备库",
    category: "数据库",
    level: "入门",
    summary: "主库通常处理写入并把变更复制到备库，备库可用于容灾或只读流量。",
    takeaway: "有备库不等于自动高可用，还需要故障检测、切换和一致性策略。",
    points: ["异步复制可能丢失尚未同步的数据", "复制延迟会导致读到旧数据", "切换前应防止双主写入"],
    commands: ["mysql -e 'SHOW REPLICA STATUS\\G'", "psql -c 'select * from pg_stat_replication;'"],
    pitfall: "只验证进程存活，不验证复制位点和数据一致性是不够的。",
    scenario: { label: "部署 MySQL 主备", template: "mysql-primary-replica" },
  },
  {
    slug: "mysql-slow-query",
    title: "MySQL 慢 SQL",
    category: "数据库",
    level: "进阶",
    summary: "慢查询可能来自缺少索引、扫描数据过多、锁等待或存储性能不足。",
    takeaway: "先用慢日志和执行计划确认瓶颈，再决定是否增加索引或改写 SQL。",
    points: ["EXPLAIN 展示访问路径", "索引不是越多越好", "锁等待与慢执行需要区别处理"],
    commands: ["mysql -e 'SHOW FULL PROCESSLIST'", "mysql -e 'SHOW ENGINE INNODB STATUS\\G'", "EXPLAIN SELECT ..."],
    pitfall: "没有评估写入成本和选择性就增加索引，可能让整体性能更差。",
    scenario: { label: "部署 MySQL 单机", template: "mysql-single" },
  },
  {
    slug: "redis-cache-failures",
    title: "缓存穿透、击穿与雪崩",
    category: "数据库",
    level: "进阶",
    summary: "三者都会让请求绕过或失去缓存保护，但产生原因和治理方法不同。",
    takeaway: "穿透针对不存在的数据，击穿针对热点键，雪崩通常是大量缓存同时失效。",
    points: ["穿透可使用空值缓存或布隆过滤器", "击穿可使用互斥更新或逻辑过期", "雪崩可分散过期时间并实施限流降级"],
    commands: ["redis-cli INFO stats", "redis-cli INFO memory", "redis-cli --bigkeys"],
    pitfall: "缓存故障时直接把全部流量压到数据库可能导致级联故障。",
    scenario: { label: "部署 Redis Sentinel", template: "redis-sentinel" },
  },
  {
    slug: "message-backlog",
    title: "消息为什么会积压",
    category: "中间件",
    level: "进阶",
    summary: "生产速度持续高于消费速度，或消费者异常、下游变慢时，待处理消息会不断增加。",
    takeaway: "积压只是现象，应分别检查生产速率、消费能力、分区并行度和下游依赖。",
    points: ["临时扩消费者不一定有效", "分区数可能限制并行度", "消费失败与反复重试会放大积压"],
    commands: ["rabbitmqctl list_queues name messages_ready messages_unacknowledged", "rabbitmqctl list_consumers"],
    pitfall: "未保证幂等性就重复消费，可能造成重复扣款或重复写入。",
    scenario: { label: "部署 RabbitMQ", template: "rabbitmq-single" },
  },
  {
    slug: "metrics-logs-traces",
    title: "指标、日志与链路追踪",
    category: "可观测性",
    level: "入门",
    summary: "指标告诉你是否异常，链路帮助定位慢在哪一段，日志解释具体发生了什么。",
    takeaway: "三种信号互相补充，不能用一套超大日志平台代替完整可观测性。",
    points: ["Metrics 适合趋势、聚合和告警", "Logs 保存离散事件和上下文", "Traces 展示请求跨服务的调用路径"],
    commands: ["curl http://localhost:9090/-/healthy", "journalctl -f", "curl http://localhost:9100/metrics | head"],
    pitfall: "采集一切却没有统一标签和服务标识，故障时仍然无法关联数据。",
    scenario: { label: "部署主机监控", template: "prometheus-node-monitoring" },
  },
  {
    slug: "sli-slo-sla",
    title: "SLI、SLO 与 SLA",
    category: "SRE",
    level: "进阶",
    summary: "SLI 是观测指标，SLO 是内部可靠性目标，SLA 是可能包含赔偿约束的服务承诺。",
    takeaway: "SRE 关注用户真正感受到的可用性、延迟和正确性，而不只是服务器是否在线。",
    points: ["SLI 必须可度量", "SLO 应有明确时间窗口", "错误预算用于平衡可靠性和变更速度"],
    commands: ["# 示例：过去 30 天成功请求数 / 总请求数", "# availability = good_events / valid_events"],
    pitfall: "把 CPU 使用率直接当成业务 SLI，通常无法代表用户体验。",
  },
  {
    slug: "rto-rpo",
    title: "RTO 与 RPO",
    category: "SRE",
    level: "进阶",
    summary: "RTO 表示业务需要多快恢复，RPO 表示最多能接受丢失多长时间的数据。",
    takeaway: "容灾设计必须把恢复时间和数据损失目标写成可验证指标。",
    points: ["低 RTO 通常需要自动切换", "低 RPO 通常需要更强的同步和备份能力", "目标越严格，建设成本通常越高"],
    commands: ["# 演练时记录故障发生、服务恢复和最后完整数据时间"],
    pitfall: "拥有备份不代表满足 RTO；从未验证过的备份也不能证明可以恢复。",
  },
  {
    slug: "rolling-blue-green-canary",
    title: "滚动、蓝绿与灰度发布",
    category: "DevOps",
    level: "进阶",
    summary: "滚动发布逐批替换实例，蓝绿发布切换整套环境，灰度发布先让少量流量进入新版本。",
    takeaway: "发布策略要同时考虑容量、兼容性、观测能力和回滚速度。",
    points: ["滚动发布资源成本较低", "蓝绿发布回滚直接但资源成本高", "灰度发布需要流量划分和指标判断"],
    commands: ["kubectl rollout status deployment/<名称>", "kubectl rollout history deployment/<名称>", "kubectl rollout undo deployment/<名称>"],
    pitfall: "数据库不可逆变更可能让应用回滚后仍然无法运行。",
    scenario: { label: "部署 Kubernetes 集群", template: "kubernetes-cluster" },
  },
  {
    slug: "kubernetes-pod-status",
    title: "Pod Running 不等于业务正常",
    category: "云原生",
    level: "入门",
    summary: "Running 只说明 Pod 已被调度且容器处于运行状态，业务仍可能未就绪或持续报错。",
    takeaway: "要结合 Ready、探针、事件、日志和真实业务请求判断健康状态。",
    points: ["Readiness 决定是否接收 Service 流量", "Liveness 用于检测是否需要重启", "Startup Probe 保护启动较慢的应用"],
    commands: ["kubectl get pods -o wide", "kubectl describe pod <名称>", "kubectl logs <名称> --previous", "kubectl get events --sort-by=.lastTimestamp"],
    pitfall: "探针配置错误会把健康应用不断重启，或让异常应用继续接收流量。",
    scenario: { label: "部署 Kubernetes 集群", template: "kubernetes-cluster" },
  },
  {
    slug: "container-image-pull",
    title: "ImagePullBackOff 怎么排查",
    category: "云原生",
    level: "进阶",
    summary: "节点拉取镜像失败后会逐渐延长重试间隔，常见于镜像名、权限、网络或镜像架构问题。",
    takeaway: "先看 Pod Events 中的原始拉取错误，再检查仓库、凭据和节点网络。",
    points: ["ErrImagePull 是本次失败", "ImagePullBackOff 是进入退避重试", "私有仓库通常需要 imagePullSecrets"],
    commands: ["kubectl describe pod <名称>", "crictl pull <镜像>", "kubectl get secret"],
    pitfall: "在控制节点能拉取镜像，不代表工作节点也具备相同网络和凭据。",
    scenario: { label: "部署 K3s 集群", template: "k3s-cluster" },
  },
  {
    slug: "backup-vs-disaster-recovery",
    title: "备份不等于容灾",
    category: "架构",
    level: "入门",
    summary: "备份保存可恢复的数据副本；容灾还需要计算、网络、切换流程和持续演练。",
    takeaway: "真正的恢复能力必须通过定期恢复测试证明，而不是通过备份任务成功证明。",
    points: ["备份应与生产故障域隔离", "恢复流程需要明确依赖和权限", "容灾演练要验证业务而不只是数据库进程"],
    commands: ["# 检查备份时间、校验和、保留策略与最近一次恢复演练结果"],
    pitfall: "同一磁盘上的备份会与原始数据一起因磁盘故障而丢失。",
  },
];

export const knowledgeCards = cards.map((card) => ({ ...card, example: knowledgeExamples[card.slug] }));
export const knowledgeCategories = ["全部", ...new Set(knowledgeCards.map((card) => card.category))];
export const findKnowledgeCard = (slug) => knowledgeCards.find((card) => card.slug === slug);
